import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportError } from "@/lib/sentry";
import { isChunkLoadError, reloadOnceForNewVersion } from "@/lib/lazyWithRetry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isStaleChunk: boolean;
}

/**
 * Minimal root error boundary. Intentionally NOT Sentry.ErrorBoundary so the
 * fallback UI is decoupled from the SDK and keeps working even if Sentry
 * itself misbehaves.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, isStaleChunk: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isStaleChunk: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      // New version deployed while the tab was open — recover silently.
      reloadOnceForNewVersion();
      return;
    }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    reportError(error, { componentStack: info.componentStack });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.isStaleChunk) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full text-center space-y-4">
            <h1 className="text-2xl font-semibold text-foreground">
              A new version is available
            </h1>
            <p className="text-sm text-muted-foreground">
              Refreshing to load the latest version…
            </p>
            <button
              onClick={this.handleReload}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Refresh now
            </button>
          </div>
        </div>
      );
    }

    if (this.props.fallback) return this.props.fallback;


    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-semibold text-foreground">
            Something went wrong
          </h1>
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred. Please reload the page to continue.
          </p>
          {this.state.error?.message && (
            <pre className="text-xs text-left bg-muted text-muted-foreground p-3 rounded-md overflow-auto max-h-40">
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={this.handleReload}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
